import type {PerkCard as PerkCardType} from '@cohort/components-xps/components/cards/PerkCard';
import TrackedPerkCard from '@cohort/wallet/components/card/TrackedPerkCard';
import {useGeneratePrivateContentUrl} from '@cohort/wallet/hooks/api/Ownerships';
import useClickTracker from '@cohort/wallet/hooks/clickTracker';
import useScreenSizeContext from '@cohort/wallet/hooks/useScreenSize';
import useSpaceContext from '@cohort/wallet/hooks/useSpaceContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {getPerkDetailRoute} from '@cohort/wallet/lib/Pages';
import type {OwnershipWDto} from '@cohort/wallet-schemas/ownership';
import type {PerkAccessWDto} from '@cohort/wallet-schemas/perkAccess';
import React from 'react';
import {useNavigate} from 'react-router-dom';

type PerkCardSingleInstanceProps = {
  perkAccess: PerkAccessWDto;
  ownership: OwnershipWDto;
};

const PerkCardSingleInstance: React.FC<PerkCardSingleInstanceProps> = ({perkAccess, ownership}) => {
  const navigate = useNavigate();
  const {hasDarkBg, accentColor} = useThemeContext();
  const {isMobile} = useScreenSizeContext();
  const {setTransitioning} = useSpaceContext();
  const {perk, canBeUsed} = perkAccess;
  const trackingCallback = useClickTracker('perks.list.card', {
    ownershipId: ownership.id,
    accessId: perkAccess.id,
    digitalAssetId: ownership.digitalAsset.id,
    perkId: perk.id,
  });

  const {data: privateContentUrlResponse} = useGeneratePrivateContentUrl(
    ownership.id,
    perkAccess.id,
    perk.privateContentFileType ?? '',
    perk.videoFileKey === null && perk.privateContentFileType === 'video'
  );

  const privateContentVideoUrl =
    perk.privateContentFileType === 'video' ? privateContentUrlResponse?.url : null;

  const perkCard: PerkCardType = {
    perkVideoFileKey: perk.videoFileKey ?? privateContentVideoUrl ?? null,
    perkThumbnailFileKey: perk.thumbnailFileKey ?? perk.privateContentThumbnailFileKey ?? null,
    perkDisplayName: perk.displayName,
    canBeUsed,
    digitalAssetImageFileKey: ownership.digitalAsset.imageFileKey,
    perkBannerFileKey: perk.bannerFileKey,
    handleAction: () => {
      trackingCallback();
      setTransitioning({active: true, enterFrom: 'left'});
      navigate(getPerkDetailRoute(perk.id, ownership.digitalAsset.id));
    },
    hasDarkBg,
    accentColor,
    raffleStatus: perkAccess.raffleStatus,
    statusMessage: perkAccess.statusMessage,
  };

  perkCard.hideMediaCover =
    isMobile && perkCard.perkBannerFileKey === null && perkCard.perkThumbnailFileKey === null;

  return (
    <TrackedPerkCard
      perkCard={perkCard}
      metadata={{
        ownershipId: ownership.id,
        accessId: perkAccess.id,
        digitalAssetId: ownership.digitalAsset.id,
        perkId: perk.id,
      }}
    />
  );
};

export default PerkCardSingleInstance;
